import type React from "react";
import styled from "styled-components";
import { Typography } from "../Typography";

export type NotificationType = "success" | "error" | "info";

export type Notification = {
	id: string;
	title: string;
	message: string;
	type: "success" | "error" | "info";
	primaryAction?: {
		label: string;
		onClick: () => void;
	};
	secondaryAction?: {
		label: string;
		onClick: () => void;
	};
};

export type NotificationProps = Notification & {
	onClose: (id: string) => void;
};

const NotificationWrapper = styled.div<{ $type: NotificationType }>`
	background-color: ${({ $type }) =>
		$type === "success"
			? "var(--on-primary)"
			: $type === "error"
			? "var(--error-container)"
			: "var(--surface)"};
	color: var(--on-surface);
	border-radius: 8px;
	margin-bottom: 0.5rem;
	display: flex;
	flex-direction: column;
	gap: 1rem;
	min-width: 300px;

	border: 1px solid #013d22;

	padding: 1rem;

	@media (min-width: 1024px) {
		padding: 1.5rem;
	}
`;

const ActionsWrapper = styled.div`
	display: flex;
	gap: 8px;
`;

const Button = styled.button<{ $isPrimary?: boolean }>`
	background-color: ${({ $isPrimary }) =>
		$isPrimary ? "var(--primary)" : "transparent"};
	color: ${({ $isPrimary }) =>
		$isPrimary ? "var(--on-primary)" : "var(--primary)"};
	border: none;
	border-radius: 4px;
	padding: 8px 16px;
	cursor: pointer;
	font-size: 14px;
	font-weight: 500;
	transition: background-color 0.2s, color 0.2s;

	&:hover {
		background-color: ${({ $isPrimary }) =>
			$isPrimary ? "var(--primary-dark)" : "var(--primary-container)"};
	}
`;

export const Notification: React.FC<NotificationProps> = ({
	id,
	title,
	message,
	type,
	primaryAction,
	secondaryAction,
	onClose,
}) => {
	return (
		<NotificationWrapper $type={type}>
			<Typography
				variant="textLG"
				color={type !== "success" ? "" : "var(--primary)"}
			>
				{title}
			</Typography>
			<Typography variant="p" color="var(--on-surface)">
				{message}
			</Typography>
			<ActionsWrapper>
				{primaryAction && (
					<Button
						$isPrimary
						onClick={() => {
							primaryAction.onClick();
							onClose(id);
						}}
					>
						{primaryAction.label}
					</Button>
				)}
				{secondaryAction && (
					<Button
						onClick={() => {
							secondaryAction.onClick();
							onClose(id);
						}}
					>
						{secondaryAction.label}
					</Button>
				)}
			</ActionsWrapper>
		</NotificationWrapper>
	);
};

const NotificationContainer = styled.div`
	position: fixed;
	z-index: 1000;
	display: flex;
	flex-direction: column;
	align-items: flex-end;

	top: 0;
	right: 0;
	margin: 10px;

	@media (min-width: 1024px) {
		top: 20px;
		right: 20px;
		marign: 0;
	}
`;

export type NotificationManagerProps = {
	notifications: NotificationProps[];
	onClose: (id: string) => void;
};

export const NotificationManager: React.FC<NotificationManagerProps> = ({
	notifications,
	onClose,
}) => {
	return (
		<NotificationContainer>
			{notifications.map((notification) => (
				<Notification
					key={notification.id}
					{...notification}
					onClose={onClose}
				/>
			))}
		</NotificationContainer>
	);
};
